import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Trash2 } from 'lucide-react';
import Layout from '../../components/Layout';
import { parseApiError } from '../../utils/errorUtils';
import {
  purchaseOrderService,
  PurchaseOrderDTO,
  PurchaseOrderStatus,
} from '../../services/purchaseOrderService';
import './PurchaseOrderPage.css';

const STATUS_BADGE: Record<PurchaseOrderStatus, string> = {
  Draft: 'ims-badge ims-badge-draft',
  Submitted: 'ims-badge ims-badge-submitted',
  Confirmed: 'ims-badge ims-badge-confirmed',
  PartiallyReceived: 'ims-badge ims-badge-partially-received',
  Received: 'ims-badge ims-badge-received',
  Closed: 'ims-badge ims-badge-closed',
};

const STATUS_I18N: Record<PurchaseOrderStatus, string> = {
  Draft: 'purchaseOrder.statusDraft',
  Submitted: 'purchaseOrder.statusSubmitted',
  Confirmed: 'purchaseOrder.statusConfirmed',
  PartiallyReceived: 'purchaseOrder.statusPartiallyReceived',
  Received: 'purchaseOrder.statusReceived',
  Closed: 'purchaseOrder.statusClosed',
};

const PurchaseOrderDeletePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [order, setOrder] = useState<PurchaseOrderDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadOrder();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadOrder = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await purchaseOrderService.getPurchaseOrderById(id || '0');
      const po = response?.data?.data;
      if (po) {
        // Guard: only Draft orders that haven't been submitted can be deleted
        if (po.status !== 'Draft' || po.approvalStatus !== 'Draft') {
          navigate(`/purchase-orders/${po.id}`, {
            replace: true,
            state: { errorMsg: t('purchaseOrder.deleteNotAllowed', 'This purchase order cannot be deleted. Only Draft orders that have not been submitted for approval can be deleted.') },
          });
          return;
        }
        setOrder(po);
      }
      setLoading(false);
    } catch {
      setError(t('purchaseOrder.failedToLoadOrder'));
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!order) return;
    setIsDeleting(true);
    try {
      await purchaseOrderService.deletePurchaseOrder(order.id);
      navigate('/purchase-orders', {
        state: { message: t('purchaseOrder.purchaseOrderDeletedSuccess') },
      });
    } catch (err: unknown) {
      setError(parseApiError(err, t('purchaseOrder.failedToDeleteOrder')).message);
      setIsDeleting(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="ims-loading">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </Layout>
    );
  }

  if (!order) {
    return (
      <Layout>
        <div style={{ padding: 32 }}>
          <div className="ims-alert ims-alert-error">{error || t('purchaseOrder.purchaseOrderNotFound')}</div>
        </div>
      </Layout>
    );
  }

  const langCode = t('common.languageCode') === 'ja' ? 'ja-JP' : 'en-US';

  return (
    <Layout>
      <div className="po-delete-page">
        <nav style={{ padding: '16px 24px 0' }}>
          <button
            className="ims-back-btn"
            onClick={() => navigate(`/purchase-orders/${id}`)}
          >
            <ArrowLeft size={16} strokeWidth={2} />
            {t('purchaseOrder.backToPurchaseOrder')}
          </button>
        </nav>

        <div className="po-delete-content">
          <div className="ims-card">
            <div className="ims-card-header">
              <Trash2 size={24} strokeWidth={2} />
              <h2 className="ims-card-title">{t('purchaseOrder.deleteConfirmationTitle')}</h2>
            </div>

            {error && (
              <div className="ims-alert ims-alert-error" style={{ margin: '16px 20px 0' }}>
                {error}
                <button type="button" onClick={() => setError(null)} style={{ marginInlineStart: 'auto', background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'inherit' }}>✕</button>
              </div>
            )}

            <div className="ims-card-body">
              <div className="po-delete-warning">
                <p style={{ margin: 0 }}>
                  {t('purchaseOrder.deleteConfirmationMessage')}
                </p>
              </div>

              <div className="po-delete-info-grid">
                <div className="po-delete-info-item">
                  <label className="po-delete-info-label">{t('purchaseOrder.poNumber')}</label>
                  <p className="po-delete-info-value"><code>{order.poNumber}</code></p>
                </div>
                <div className="po-delete-info-item">
                  <label className="po-delete-info-label">{t('purchaseOrder.supplier')}</label>
                  <p className="po-delete-info-value">{order.supplierName || '—'}</p>
                </div>
                <div className="po-delete-info-item">
                  <label className="po-delete-info-label">{t('purchaseOrder.orderDate')}</label>
                  <p className="po-delete-info-value">
                    {new Date(order.orderDate).toLocaleDateString(langCode)}
                  </p>
                </div>
                <div className="po-delete-info-item">
                  <label className="po-delete-info-label">{t('purchaseOrder.status')}</label>
                  <p className="po-delete-info-value">
                    <span className={STATUS_BADGE[order.status] ?? 'ims-badge ims-badge-draft'}>
                      {t(STATUS_I18N[order.status] ?? 'purchaseOrder.statusDraft')}
                    </span>
                  </p>
                </div>
                <div className="po-delete-info-item">
                  <label className="po-delete-info-label">{t('purchaseOrder.totalAmount')}</label>
                  <p className="po-delete-info-value" style={{ fontWeight: 600 }}>
                    {new Intl.NumberFormat(langCode, { style: 'decimal', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(order.totalAmount)}
                  </p>
                </div>
              </div>

              <div className="po-delete-actions">
                <button
                  className="btn-cancel"
                  onClick={() => navigate(`/purchase-orders/${id}`)}
                  disabled={isDeleting}
                >
                  {t('common.cancel')}
                </button>
                <button
                  className="btn-delete"
                  onClick={handleDelete}
                  disabled={isDeleting}
                >
                  {isDeleting ? (
                    <><span className="ims-btn-spinner" />{t('common.deleting')}</>
                  ) : (
                    <>
                      <Trash2 size={16} strokeWidth={2} />
                      {t('purchaseOrder.deleteItem')}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          <div className="ims-card">
            <div className="ims-card-header">
              <h3 className="ims-card-title">{t('purchaseOrder.safetyInformation')}</h3>
            </div>
            <div className="ims-card-body">
              <div className="po-help-section">
                <div className="po-help-item">
                  <strong>{t('purchaseOrder.permanentAction')}</strong>
                  <p>{t('purchaseOrder.deleteWarning')}</p>
                </div>
                <div className="po-help-item">
                  <strong>{t('purchaseOrder.relatedData')}</strong>
                  <p>{t('purchaseOrder.deleteRelatedDataWarning')}</p>
                </div>
                <div className="po-help-item">
                  <strong>{t('purchaseOrder.confirmationRequired')}</strong>
                  <p>{t('purchaseOrder.deleteConfirmationRequired')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default PurchaseOrderDeletePage;
